import React from "react";
import { IAirCooledChillerReading } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import {
  IStackTokens,
  PanelType,
  Stack,
  Toggle,
  DefaultButton,
  PrimaryButton,
  Label,
  DatePicker,
  DayOfWeek,
} from "office-ui-fabric-react";
import { getAlertDetails } from "../../../services/ThresholdService";
import CustomDrawer from "../../common/CustomDrawer";
import * as yup from "yup";
import { Formik } from "formik";
import { CustomTextField } from "../../common/Library";
import { CategoryHeader } from "../Misc";
import { updateAccReading } from "../../../services/infrastructure/AccService";
import Datetime from "react-datetime";

const evaporator400TRSchema = yup.object().shape({
  chwInletPressure: yup.number().required("required"),
  chwOutletPressure: yup.number().required("required"),
  activeChwSetPoint: yup.number().required("required"),
  chwInletTemperature: yup.number().required("required"),
  chwOutletTemperature: yup.number().required("required"),
  refrigerantPressureCkt01: yup.number().required("required"),
  refrigerantPressureCkt02: yup.number().required("required"),
  suctionEductorTempCkt01: yup.number().required("required"),
  suctionEductorTempCkt02: yup.number().required("required"),
});

const evaporator150TRSchema = yup.object().shape({
  activeChwSetPoint: yup.number().required("required"),
  chwInletTemperature: yup.number().required("required"),
  chwOutletTemperature: yup.number().required("required"),
  refrigerantPressureCkt01: yup.number().required("required"),
  refrigerantPressureCkt02: yup.number().required("required"),
  saturatedEvaporatorRefrigerantTempCkt01: yup.number().required("required"),
  saturatedEvaporatorRefrigerantTempCkt02: yup.number().required("required"),
  approachTempCkt01: yup.number().required("required"),
  approachTempCkt02: yup.number().required("required"),
  outdoorAirTemperature: yup.number().required("required"),
});

const condenser400TRSchema = yup.object().shape({
  dischargePressureCkt01: yup.number().required("required"),
  dischargePressureCkt02: yup.number().required("required"),
  dischargeTempCkt01: yup.number().required("required"),
  dischargeTempCkt02: yup.number().required("required"),
  refrigerantSaturatedTempCkt01: yup.number().required("required"),
  refrigerantSaturatedTempCkt02: yup.number().required("required"),
  subCoolingTempCkt01: yup.number().required("required"),
  subCoolingTempCkt02: yup.number().required("required"),
  oilPressureCkt01: yup.number().required("required"),
  oilPressureCkt02: yup.number().required("required"),
  dischargeSuperHeatCkt01: yup.number().required("required"),
  dischargeSuperHeatCkt02: yup.number().required("required"),
});

const compressor400TRSchema = yup.object().shape({
  runningTimeCkt01: yup.number().required("required"),
  runningTimeCkt02: yup.number().required("required"),
  speedCkt01: yup.number().required("required"),
  speedCkt02: yup.number().required("required"),
});

const compressor150TRSchema = yup.object().shape({
  ampCkt01: yup.number().required("required"),
  ampCkt02: yup.number().required("required"),
  percentageRlaCkt01: yup.number().required("required"),
  percentageRlaCkt02: yup.number().required("required"),
  intermediateOilPressureCkt01: yup.number().required("required"),
  intermediateOilPressureCkt02: yup.number().required("required"),
  unitVoltsCkt01: yup.number().required("required"),
  unitVoltsCkt02: yup.number().required("required"),
  oilTempCkt01: yup.number().required("required"),
  oilTempCkt02: yup.number().required("required"),
});

const motorVsd400TRSchema = yup.object().shape({
  dcBusVoltageCkt01: yup.number().required("required"),
  dcBusVoltageCkt02: yup.number().required("required"),
  vsdCommand: yup.number().required("required"),
  vsdActual: yup.number().required("required"),
  motorCurrentCkt01: yup.number().required("required"),
  motorCurrentCkt02: yup.number().required("required"),
  IGBTBasePlateTempCkt01: yup.number().required("required"),
  IGBTBasePlateTempCkt02: yup.number().required("required"),
  compressor01kw: yup.number().required("required"),
  compressor02kw: yup.number().required("required"),
});

const fan400TRSchema = yup.object().shape({
  vfdFanSpeedCkt01: yup.number().required("required"),
  vfdFanSpeedCkt02: yup.number().required("required"),
  fanKwtCkt01: yup.number().required("required"),
  fanKwtCkt02: yup.number().required("required"),
  ambientTemp: yup.number().required("required"),
});

const accReading400TRSchema = yup.object().shape({
  evaporator: evaporator400TRSchema,
  condenser: condenser400TRSchema,
  compressor: compressor400TRSchema,
  motorVSD: motorVsd400TRSchema,
  fan: fan400TRSchema,
  recordedBy: yup.string().required("required"),
  remarks: yup.string().optional(),
});

const accReading150TRSchema = yup.object().shape({
  evaporator: evaporator150TRSchema,
  compressor: compressor150TRSchema,
  recordedBy: yup.string().required("required"),
  remarks: yup.string().optional(),
});

export interface ManageAccDialogProps {
  data?: IAirCooledChillerReading;
  handleLoading: any;
  handleDialogClose: any;
  threshold: any;
}

const ManageAccDialog: React.FC<ManageAccDialogProps> = ({
  data,
  handleLoading,
  handleDialogClose,
  threshold,
}) => {
  //state
  const [state, setState] = React.useState({
    accReading: {
      ...data,
    },
    isLoading: false,
  } as {
    accReading: IAirCooledChillerReading;
    isLoading: boolean;
  });

  //hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  const wrapStackTokens: IStackTokens = { childrenGap: 8 };

  const closeUpdateDialoge = (data?: IAirCooledChillerReading) => {
    handleDialogClose(data);
  };

  const onBlurFunc = (value: any, path: string) => {
    console.log("onBlurFunc");
    const { showAlert, msg } = getAlertDetails(path, threshold, value);
    console.log("[showAlert]", showAlert, msg);
    if (showAlert === true) {
      showConfirmDialog("Alert", msg as string, () => { }, "Ok", true);
    }
  };

  return (
    <React.Fragment>
      <CustomDrawer
        drawerType={PanelType.large}
        title={"Update Air Cooled Chiller Reading"}
        handelOpen={true}
        onClose={() => closeUpdateDialoge()}
      >
        <Stack>
          <Formik
            initialValues={state.accReading}
            // validationSchema={
            //   state.accReading.airType === "400 TR"
            //     ? accReading400TRSchema
            //     : accReading150TRSchema
            // }
            onSubmit={async (
              values: IAirCooledChillerReading,
              { setSubmitting }
            ) => {
              let obj = { ...values };
              let result;
              handleLoading();
              result = await updateAccReading(obj);
              handleLoading();
              if (result?.success) {
                closeUpdateDialoge(result.data);
                showToast(
                  `Air Cooled Chiller Reading updated successfully`,
                  "success"
                );
              } else {
                showToast(
                  `Error while updating Air Cooled Chiller  reading`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, values, setFieldValue }) => (
              <form onSubmit={handleSubmit}>
                <div className="ms-Grid">
                  <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-xs12 ms-sm12 ms-md12 custom-toggle">
                      <Toggle
                        checked={values.isSystemOn}
                        onText="On"
                        offText="Off"
                        onChange={(e, v) => setFieldValue("isSystemOn", v)}
                        label="System"
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CategoryHeader title="EVAPORATOR" />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"evaporator.activeChwSetPoint"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Active CHW Setpoint"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"evaporator.chwInletTemperature"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"CHW Inlet/Entering Temp"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"evaporator.chwOutletTemperature"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"CHW Outlet/Leaving Temp"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"evaporator.refrigerantPressureCkt01"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Refrigerant Pressure CKT 01"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"evaporator.refrigerantPressureCkt02"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Refrigerant Pressure CKT 02"}
                      />
                    </div>

                    {state.accReading.airType === "400 TR" && (
                      <>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"evaporator.suctionEductorTempCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Suction Eductor Temp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"evaporator.suctionEductorTempCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Suction Eductor Temp CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"evaporator.chwInletPressure"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"CHW Inlet Pressure"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"evaporator.chwOutletPressure"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"CHW Outlet Pressure"}
                          />
                        </div>
                      </>
                    )}

                    {state.accReading.airType === "150 TR" && (
                      <>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={
                              "evaporator.saturatedEvaporatorRefrigerantTempCkt01"
                            }
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Saturated Refrigerant Temp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={
                              "evaporator.saturatedEvaporatorRefrigerantTempCkt02"
                            }
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Saturated Refrigerant Temp CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"evaporator.approachTempCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Approach Temp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"evaporator.approachTempCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Approach Temp CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"evaporator.outdoorAirTemperature"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Outdoor Air Temperature"}
                          />
                        </div>
                      </>
                    )}

                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CategoryHeader title="CONDENSER" />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"condenser.dischargePressureCkt01"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Discharge Pressure CKT 01"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"condenser.dischargePressureCkt02"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Discharge Pressure CKT 02"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"condenser.dischargeTempCkt01"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Discharge Temp CKT 01"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"condenser.dischargeTempCkt02"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Discharge Temp CKT 02"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"condenser.refrigerantSaturatedTempCkt01"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Refrigerent Saturated Temp CKT 01"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"condenser.refrigerantSaturatedTempCkt02"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Refrigerent Saturated Temp CKT 02"}
                      />
                    </div>

                    {state.accReading.airType === "400 TR" && (
                      <>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"condenser.subCoolingTempCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Sub Cooling Temp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"condenser.subCoolingTempCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Sub Cooling Temp CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"condenser.oilPressureCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Oil Pressure CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"condenser.oilPressureCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Oil Pressure CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"condenser.dischargeSuperHeatCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Discharge Super Heat CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"condenser.dischargeSuperHeatCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Discharge Super Heat CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"condenser.subCoolingCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Sub Cooling CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"condenser.subCoolingCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Sub Cooling CKT 02"}
                          />
                        </div>
                      </>
                    )}

                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CategoryHeader title="COMPRESSOR" />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-datepicker">
                      {/* <DatePicker
                        firstDayOfWeek={DayOfWeek.Sunday}
                        showMonthPickerAsOverlay={true}
                        value={new Date(values.compressor.startCkt01)}
                        showGoToToday={false}
                        onSelectDate={(date: Date | null | undefined) =>
                          setFieldValue("compressor.startCkt01", date)
                        }
                        placeholder="Select a date..."
                        ariaLabel="Select a date"
                        label="Starts Time CKT 01"
                      /> */}
                      <label className="ms-Label">Starts Time CKT 01</label>
                      <Datetime
                        className="custom-field-group ms-TextField-fieldGroup "
                        inputProps={{
                          className: "ms-TextField-field px-8 border-0 w-100",
                        }}
                        dateFormat="ddd MMM DD YYYY"
                        timeFormat="HH:mm"
                        value={new Date(values.compressor.startCkt01)}
                        onChange={(date) => {
                          setFieldValue("compressor.startCkt01", date);
                        }}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-datepicker">
                      {/* <DatePicker
                        firstDayOfWeek={DayOfWeek.Sunday}
                        showMonthPickerAsOverlay={true}
                        value={new Date(values.compressor.startCkt02)}
                        showGoToToday={false}
                        onSelectDate={(date: Date | null | undefined) =>
                          setFieldValue("compressor.startCkt02", date)
                        }
                        placeholder="Select a date..."
                        ariaLabel="Select a date"
                        label="Starts Time CKT 02"
                      /> */}
                      <label className="ms-Label">Starts Time CKT 02</label>
                      <Datetime
                        className="custom-field-group ms-TextField-fieldGroup "
                        inputProps={{
                          className: "ms-TextField-field px-8 border-0 w-100",
                        }}
                        dateFormat="ddd MMM DD YYYY"
                        timeFormat="HH:mm"
                        value={new Date(values.compressor.startCkt02)}
                        onChange={(date) => {
                          setFieldValue("compressor.startCkt02", date);
                        }}
                      />
                    </div>

                    {state.accReading.airType === "400 TR" && (
                      <>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.runningTimeCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Running Time CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.runningTimeCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Running Time CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.speedCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Compressor Speed CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.speedCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Compressor Speed CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                          <Toggle
                            checked={
                              values.compressor
                                .isOilLevelAboveBottomSightGlassCkt01
                            }
                            onText="Yes"
                            offText="No"
                            onChange={(e, v) =>
                              setFieldValue(
                                "compressor.isOilLevelAboveBottomSightGlassCkt01",
                                v
                              )
                            }
                            label="Oil Level CKT 01"
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                          <Toggle
                            checked={
                              values.compressor
                                .isOilLevelAboveBottomSightGlassCkt02
                            }
                            onText="Yes"
                            offText="No"
                            onChange={(e, v) =>
                              setFieldValue(
                                "compressor.isOilLevelAboveBottomSightGlassCkt02",
                                v
                              )
                            }
                            label="Oil Level CKT 02"
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.dischargeTempCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Discharge Temp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.dischargeTempCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Discharge Temp CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.saturationDischargeTempCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Saturation Discharge Temp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.saturationDischargeTempCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Saturation Discharge Temp CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.dischargeSuperHeatCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Discharge Super Heat CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.dischargeSuperHeatCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Discharge Super Heat CKT 02"}
                          />
                        </div>
                      </>
                    )}

                    {state.accReading.airType === "150 TR" && (
                      <>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.ampCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Amp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.ampCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Amp CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.percentageRlaCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Percentage RLA CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.percentageRlaCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Percentage RLA CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.intermediateOilPressureCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Intermediate Oil Pressure CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.intermediateOilPressureCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Intermediate Oil Pressure CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.unitVoltsCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Unit Volts CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.unitVoltsCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Unit Volts CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.oilTempCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Oil Temp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"compressor.oilTempCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Oil Temp CKT 02"}
                          />
                        </div>
                      </>
                    )}

                    {state.accReading.airType === "400 TR" && (
                      <>
                        <div className="ms-Grid-col ms-xs12 ms-sm12">
                          <CategoryHeader title="MOTOR VSD" />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.dcBusVoltageCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Dc Bus Voltage CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.dcBusVoltageCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Dc Bus Voltage CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.vsdCommand"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"VSD Command"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.vsdActual"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"VSD Actual"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.motorCurrentCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Motor Current CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.motorCurrentCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Motor Current CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.IGBTBasePlateTempCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"IGBT Base Plate Temp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.IGBTBasePlateTempCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"IGBT Base Plate Temp CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.compressor01kw"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"compressor 01 KW"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.compressor02kw"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"compressor 02 KW"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.flaCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Fla CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.flaCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Fla CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.commandFreqCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Command Freq CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.commandFreqCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Command Freq CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.actualFreqCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Actual Freq CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.actualFreqCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Actual Freq CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.internalAmbientTempCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Internal Ambient Temp CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"motorVSD.internalAmbientTempCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Internal Ambient Temp CKT 02"}
                          />
                        </div>

                        <div className="ms-Grid-col ms-xs12 ms-sm12">
                          <CategoryHeader title="FAN" />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"fan.vfdFanSpeedCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"VFD Fan Speed CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"fan.vfdFanSpeedCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"VFD Fan Speed CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"fan.ambientTemp"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Ambient Temp"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"fan.fanKwtCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Fan KW CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"fan.fanKwtCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Fan KW CKT 02"}
                          />
                        </div>

                        <div className="ms-Grid-col ms-xs12 ms-sm12">
                          <CategoryHeader title="FLASH TANK" />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"flashTank.economizerValueOpenCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Economizer Value Open CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"flashTank.economizerValueOpenCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Economizer Value Open CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"flashTank.condenserDrainValveOpenCkt01"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Condenser Drain Valve Open CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"flashTank.condenserDrainValveOpenCkt02"}
                            onBlurFunc={onBlurFunc}
                            type="number"
                            label={"Condenser Drain Valve Open CKT 02"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"flashTank.flashTankLevelCkt01"}
                            onBlurFunc={onBlurFunc}
                            // type="number"
                            label={"Flash Tank Level CKT 01"}
                          />
                        </div>
                        <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                          <CustomTextField
                            name={"flashTank.flashTankLevelCkt02"}
                            onBlurFunc={onBlurFunc}
                            // type="number"
                            label={"Flash Tank Level CKT 02"}
                          />
                        </div>
                      </>
                    )}

                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CategoryHeader title="General:" />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CustomTextField
                        name={"remarks"}
                        label={"Remarks (If Any)"}
                        multiline
                        autoAdjustHeight
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CustomTextField
                        name={"recordedBy"}
                        label={"Shift Tech Sign"}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Stack
                    horizontal
                    wrap
                    tokens={wrapStackTokens}
                    horizontalAlign="end"
                    className="pt-8"
                  >
                    <DefaultButton
                      text="Cancel"
                      onClick={() => closeUpdateDialoge()}
                      className="borderless-btn"
                    />
                    <PrimaryButton type="submit" text="Update" />
                  </Stack>
                </div>
                {/* <Stack wrap tokens={wrapStackTokens}>
                  <Stack.Item>
                    <CategoryHeader title="EVAPORATOR:" />
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="space-between"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"evaporator.chwInletPressure"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"CHW Inlet Pressure"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"evaporator.chwOutletPressure"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"CHW Outlet Pressure"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"evaporator.activeChwSetPoint"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Active CHW Setpoint"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="space-between"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"evaporator.chwInletTemperature"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"CHW Inlet/Entering Temp"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"evaporator.chwOutletTemperature"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"CHW Outlet/Leaving Temp"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"evaporator.refrigerantPressureCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Refrigerant Pressure CKT 01"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="space-between"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"evaporator.refrigerantPressureCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Refrigerant Pressure CKT 02"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"evaporator.suctionEductorTempCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Suction Eductor Temp CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"evaporator.suctionEductorTempCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Suction Eductor Temp CKT 02"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="CONDENSER:" />
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.dischargePressureCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Condenser Discharge Pressure CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.dischargePressureCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Condenser Discharge Pressure CKT 02"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.dischargeTempCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Condenser Discharge Temp CKT 01"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.dischargeTempCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Condenser Discharge Temp CKT 02"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.refrigerantSaturatedTempCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Refrigerent Saturated Temp CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.refrigerantSaturatedTempCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Refrigerent Saturated Temp CKT 02"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.subCoolingTempCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Sub Cooling Temp CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.subCoolingTempCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Sub Cooling Temp CKT 02"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.oilPressureCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Oil Pressure CKT 01"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.oilPressureCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Oil Pressure CKT 02"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.dischargeSuperHeatCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Condenser Discharge Super Heat CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"condenser.dischargeSuperHeatCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Condenser Discharge Super Heat CKT 02"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="COMPRESSOR:" />
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"compressor.runningTimeCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Running Time CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"compressor.runningTimeCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Running Time CKT 02"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"compressor.speedCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Compressor Speed CKT 01"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"compressor.speedCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Compressor Speed CKT 02"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={
                            values.compressor
                              .isOilLevelAboveBottomSightGlassCkt01
                          }
                          onText="Yes"
                          offText="No"
                          onChange={(e, v) =>
                            setFieldValue(
                              "compressor.isOilLevelAboveBottomSightGlassCkt01",
                              v
                            )
                          }
                          label="Oil Level CKT 01"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={
                            values.compressor
                              .isOilLevelAboveBottomSightGlassCkt02
                          }
                          onText="Yes"
                          offText="No"
                          onChange={(e, v) =>
                            setFieldValue(
                              "compressor.isOilLevelAboveBottomSightGlassCkt02",
                              v
                            )
                          }
                          label="Oil Level CKT 02"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="MOTOR VSD:" />
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.dcBusVoltageCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Dc Bus Voltage CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.dcBusVoltageCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Dc Bus Voltage CKT 02"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.vsdCommand"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"VSD Command"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.vsdActual"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"VSD Actual"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.motorCurrentCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Motor Current CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.motorCurrentCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Motor Current CKT 02"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.IGBTBasePlateTempCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"IGBT Base Plate Temp CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.IGBTBasePlateTempCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"IGBT Base Plate Temp CKT 02"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.compressor01kw"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"compressor 01 KW"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"motorVSD.compressor02kw"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"compressor 02 KW"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="FAN:" />
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"fan.vfdFanSpeedCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"VFD Fan Speed CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"fan.vfdFanSpeedCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"VFD Fan Speed CKT 02"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"fan.ambientTemp"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Ambient Temp"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"fan.fanKwtCkt01"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Fan KW CKT 01"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"fan.fanKwtCkt02"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Fan KW CKT 02"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="General:" />
                    <Stack.Item>
                      <CustomTextField
                        name={"remarks"}
                        label={"Remarks (If Any)"}
                        multiline
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <CustomTextField
                        name={"recordedBy"}
                        label={"Shift Tech Sign"}
                        disabled={true}
                      />
                    </Stack.Item>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="end"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeUpdateDialoge()}
                      />
                      <PrimaryButton type="submit" text="Update" />
                    </Stack>
                  </Stack.Item>
                </Stack> */}
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageAccDialog;
